import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Project } from '../data/projects';
import STLViewer from './STLViewer';

interface ProjectModalProps {
  project: Project;
  isActive: boolean;
  onClose: () => void;
}

const ProjectModal: React.FC<ProjectModalProps> = ({ project, isActive, onClose }) => {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previouslyFocusedElement = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isActive) return;

    previouslyFocusedElement.current = document.activeElement as HTMLElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
      previouslyFocusedElement.current?.focus();
    };
  }, [isActive, onClose]);

  if (!isActive) return null;

  const isVideo = project.image.toLowerCase().endsWith('.mp4');

  return createPortal(
    <div className="project-dialog" onMouseDown={event => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <div className="project-dialog__panel" role="dialog" aria-modal="true" aria-labelledby={`project-title-${project.id}`}>
        <div className="project-dialog__top">
          <span className="project-dialog__eyebrow">{project.categoryLabel}</span>
          <button ref={closeButtonRef} type="button" className="project-dialog__close" onClick={onClose} aria-label="Close project details">
            <i className="ri-close-line" aria-hidden="true" />
          </button>
        </div>
        <h2 id={`project-title-${project.id}`} className="project-dialog__title">{project.title}</h2>
        {project.stlUrl ? (
          <STLViewer urls={project.stlUrl} />
        ) : isVideo ? (
          <video className="project-dialog__media" src={project.image} autoPlay loop muted playsInline controls />
        ) : (
          <img className="project-dialog__media" src={project.image} alt={project.title} />
        )}
        <p className="project-dialog__description">{project.description}</p>
        <ul className="project-dialog__tech">
          {project.technologies.map(tech => <li key={tech}>{tech}</li>)}
        </ul>
        <div className="project-dialog__links">
          <a href={project.githubUrl} target="_blank" rel="noopener noreferrer" className="btn btn--signal">
            View Source <i className="ri-external-link-line" aria-hidden="true" />
          </a>
          {project.demoUrl && <a href={project.demoUrl} target="_blank" rel="noopener noreferrer" className="btn btn--ghost">Live Demo</a>}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ProjectModal;